import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from './Tooltip';

type Theme = 'system' | 'dark' | 'light';

const STORAGE_KEY = 'faerdo.theme';
const CYCLE: Theme[] = ['system', 'dark', 'light'];

function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    // localStorage unavailable (e.g. SSR or private mode)
  }
  return 'system';
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'system') {
    root.removeAttribute('data-theme');
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  } else {
    root.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // ignore
    }
  }
}

function useTheme() {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  function cycleTheme() {
    setThemeState((current) => {
      const idx = CYCLE.indexOf(current);
      return CYCLE[(idx + 1) % CYCLE.length];
    });
  }

  return { theme, cycleTheme };
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const iconProps = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true as const,
};

function SunIcon() {
  return (
    <svg {...iconProps}>
      <circle cx='12' cy='12' r='4' />
      <path d='M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41' />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg {...iconProps}>
      <path d='M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z' />
    </svg>
  );
}

function SystemIcon() {
  return (
    <svg {...iconProps}>
      <rect x='2' y='3' width='20' height='14' rx='2' />
      <path d='M8 21h8M12 17v4' />
    </svg>
  );
}

const THEME_ICONS: Record<Theme, () => React.ReactElement> = {
  system: SystemIcon,
  dark: MoonIcon,
  light: SunIcon,
};

// ─── Component ────────────────────────────────────────────────────────────────

export function ThemeToggle() {
  const { t } = useTranslation('common');
  const { theme, cycleTheme } = useTheme();

  const Icon = THEME_ICONS[theme];
  const label = t(`theme.${theme}`);

  return (
    <Tooltip content={label}>
      <button
        type='button'
        onClick={cycleTheme}
        aria-label={label}
        className='flex h-9 w-9 items-center justify-center rounded-full border border-border bg-bg text-fg-muted shadow-elevated transition-colors hover:border-accent-border hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg'
      >
        <Icon />
      </button>
    </Tooltip>
  );
}
